import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { getRequest, postRequest } from '../../../services/base'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { NavContext } from '../../../context/global_context'
import {
  SELECT_TRACER_MAPPING,
  SET_TR_MAPPING_LIST,
  TracerMappingContext,
} from './context/context'
import { getSessionStorageData } from '../../../utils/SessionStorage'
import Button from '../../common/Button'
import DateTimeInput from '../../common/DateTimeInput'
import {
  Empty,
  Flex,
  Form,
  Modal,
  Row,
  Segmented,
  Space,
  Table,
} from 'antd'
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import Mappings from './Mappings'
import { formatDateTime } from '../../../utils/common'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import ExpandedRowRender from './ExpandedRowRenderer'
import RecordsCount from '../../common/RecordsCount'

dayjs.extend(customParseFormat)

const TracerMapping = () => {
  let { vehicle } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const intl = useIntl()
  
  const [navState, navDispatch] = useContext(NavContext)
  const [state, dispatch] = useContext(TracerMappingContext)
  
  const [fetchedAfterDate, setFetchedAfterDate] = useState(null)
  const [recordType, setRecordType] = useState('new')
  const [mappingRecords, setMappingRecords] = useState([])
  
  const [modal, contextHolder] = Modal.useModal()
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const menuItems = [
    {
      label: intl.formatMessage({ id: 'sidebar.taMappings' }),
      value: `/vehicles/${vehicle}/dtcs/listing`,
    },
    {
      label: intl.formatMessage({ id: 'sidebar.csMappings' }),
      value: `/vehicles/${vehicle}/dtcs/tracer_mapping`,
    },
    {
      label: intl.formatMessage({ id: 'sidebar.csMemos' }),
      value: `/vehicles/${vehicle}/dtcs/memos`,
    },
  ]

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: intl.formatMessage({ id: 'layout.vehicleList' }) + ` / ${vehicle} / DTCs / ` + intl.formatMessage({ id: 'sidebar.csMappings' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: getSessionStorageData('vehicle')?.search
          ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
          : `/vehicles`,
      },
    })
  }, [])

  const tracerAction = useCallback(async (map_row) => {
    let res = await postRequest(
      `/api/tracer_mappings/update_mappings/${vehicle}/`,
      state.selected_tracer_mapping
    )
    // `if (res?.msg)` was the failure test; base.js leaves msg empty on a
    // network failure, so those went unreported entirely.
    if (res?.status === false) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'dtcs.tracerMapping.updateFailed' }),
        },
      })
      // Return: without it the error shape was written into the row's `logs`
      // below, so the Logs panel then rendered neither successes nor errors.
      return
    }

    // The backend answers with {errors: [], success: []}; when both are empty
    // nothing used to be reported, so an accept that changed nothing looked
    // identical to one that worked.
    if (!res?.errors?.length && !res?.success?.length) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'info',
          msg: intl.formatMessage({ id: 'dtcs.tracerMapping.noChanges' }),
        },
      })
    }

    if (res?.errors?.length > 0) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: res?.errors.join(', '),
        },
      })
    }
    if (res?.success?.length > 0) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: res?.success.join(', '),
        },
      })
    }

    let tempMapping = [...state.tracer_mappings]
    let tempIndex = tempMapping.findIndex(
      (tempEle) =>
        tempEle.dtc === map_row.dtc && tempEle.acronym === map_row.acronym
    )
    tempMapping[tempIndex].logs = res
    dispatch({ type: SET_TR_MAPPING_LIST, data: tempMapping })
    filterRecords(recordType, tempMapping)
  }, [state])
  
  const confirmTracerMapping = useCallback((action, index, map_row) => {
    const title = intl.formatMessage({ id: 'dtcs.mappings.confirmTracerMapping' })
    const content = intl.formatMessage({ id: 'dtcs.mappings.confirmTracerMappingContent' })

    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk: () => {
        tracerAction(map_row)
      },
      centered: true,
      maskClosable: true,
    })
  }, [tracerAction])

  const validateBeforeImport = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'dtcs.mappings.fetchingFromTracer' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await postRequest(
      `/api/tracer_mappings/tracer_mappings/${vehicle}/?vehicle=${vehicle}`,
      {
        updated_after: fetchedAfterDate,
      },
      false
    )
    
    if (res?.status) {
      if (res.data?.length > 0) {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: intl.formatMessage({ id: 'dtcs.mappings.fetchSuccess' }),
          },
        })
        dispatch({ type: SET_TR_MAPPING_LIST, data: res.data })
        filterRecords(recordType, res.data)
      } else {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: intl.formatMessage({ id: 'dtcs.mappings.dataNotFound' }, { vehicle }),
          },
        })
        setMappingRecords([])
      }
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'dtcs.tracerMapping.validateFailed' }),
        },
      })
      setMappingRecords([])
    }
  }

  const showImportConfirmationModal = (fetchedAfterDate) => {
    const title = intl.formatMessage({ id: 'dtcs.mappings.confirmImport' })
    const dateInfo = fetchedAfterDate ? intl.formatMessage({ id: 'dtcs.mappings.forSelectedDate' }) : intl.formatMessage({ id: 'dtcs.mappings.all' })
    const content = intl.formatMessage({ id: 'dtcs.mappings.confirmImportContent' }, { dateInfo })

    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk: () => {
        validateBeforeImport()
      },
      centered: true,
      maskClosable: true,
    })
  }

  const filterRecords = (record_type, records) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'dtcs.mappings.filteringMappings' }),
        showLoader: true,
      },
      notification: {},
    })
    setRecordType(record_type)
    if (record_type === 'new') {
      let filteredMappings = records.filter(
        (map_obj) =>
          new Date(map_obj.at_updated_at) < new Date(map_obj.updated_at)
      )
      setMappingRecords(filteredMappings)
    } else {
      setMappingRecords(records)
    }
    navDispatch({
      type: 'loader',
      data: {
        caption: '',
        showLoader: false,
      },
      notification: {},
    })
  }

  const dataSource = useMemo(() => {
    if(!mappingRecords?.length) return []

    return mappingRecords.map((map_row, index) => {
      return {
        key: `tracer-mapping-row-${index}`,
        rowKey: `${map_row?.dtc}-${map_row?.acronym}`,
        ...map_row,

        showAction: true,
      }
    })
  }, [mappingRecords])

  const columns = [
    {
      title: intl.formatMessage({ id: 'dtcs.mappings.dtcName' }),
      dataIndex: 'dtc',
      sorter: (a, b) => a.dtc.localeCompare(b.dtc),
      render: (text) => <EllipsisTooltip>{text}</EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'generic.description' }),
      dataIndex: 'description',
      width: 'auto',
      render: (text) => <EllipsisTooltip>{text}</EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'dtcs.mappings.ecu' }),
      dataIndex: 'acronym',
      render: (text) => <EllipsisTooltip>{text}</EllipsisTooltip>,
    },
    {
      title: intl.formatMessage({ id: 'dtcs.mappings.updated' }),
      dataIndex: 'updated_at',
      render: (text) => (
        <EllipsisTooltip>
          {dayjs(text).format('DD-MM-YYYY hh:mm A')}
        </EllipsisTooltip>
      ),
    },
    {
      title: intl.formatMessage({ id: 'dtcs.mappings.action' }),
      align: 'right',
      render: (_, record) => {
        const isExpanded = expandedRowKeys.includes(record.rowKey)
        const isSelectedItem =
          `${record?.dtc}-${record?.acronym}` ==
          `${state?.selected_tracer_mapping?.dtc}-${state?.selected_tracer_mapping?.acronym}`

        return record.showAction ? (
          <Button
            type="link"
            className={`${isExpanded ? 'link-button neutral' : 'link-button'}`}
            onClick={() => {
              toggleExpand(`${record?.dtc}-${record?.acronym}`);
              (!isSelectedItem) ? (
                dispatch({ type: SELECT_TRACER_MAPPING, data: record })
              ) : (
                dispatch({ type: SELECT_TRACER_MAPPING, data: {} })
              )
            }}
          >
            {isSelectedItem ? <>{intl.formatMessage({ id: 'dtcs.tracerMapping.close' })}</> : <>{intl.formatMessage({ id: 'forms.review' })}</>}
          </Button>
        ) : (
          <Button
            type="link"
            className="link-button"
            onClick={() => {
              record.onDelete()
            }}
          >
            {intl.formatMessage({ id: 'dtcs.tracerMapping.delete' })}
          </Button>
        )
      },
    },
  ]

  const toggleExpand = (id) => {
    setExpandedRowKeys((prev) => {
      const keys = prev.includes(id)
        ? prev.filter((key) => key !== id)
        : [id]
      return keys
    })
  }

  return (
    <>
      <div className="page tracer-mappings">
        <Row className="title">
          <Segmented
            options={menuItems}
            value={location.pathname}
            onChange={(value) => navigate(value)}
          />
        </Row>

        <Row className="body">
          <Flex justify="space-between" className="full-width">
            <Form>
              <Space size={'middle'} align="center">
                <DateTimeInput
                  name={`select-date-time-tracer`}
                  value={fetchedAfterDate}
                  placeholder={intl.formatMessage({ id: 'dtcs.mappings.selectDatePlaceholder' })}
                  showTime={{
                    defaultOpenValue: dayjs('00:00', 'HH:mm'), 
                    format: 'HH:mm'
                  }}
                  needConfirm={false}
                  isRequired={false}
                  wrapperClass="date-time"
                  onChange={(value) => {
                    setFetchedAfterDate(value)
                  }}
                />

                <Button
                  htmlType='submit'
                  onClick={() => {
                    showImportConfirmationModal(fetchedAfterDate)
                  }}
                  testId="tracer-mapping-import"
                >
                  {intl.formatMessage({ id: 'dtcs.mappings.importAllFromTracer' })}
                </Button>
              </Space>
            </Form>

            {recordType == 'new' ? (
              <Button
                type='primary'
                onClick={(e) => {
                  filterRecords('all', state?.tracer_mappings)
                }}
              >
                {intl.formatMessage({ id: 'dtcs.mappings.showAllRecords' })}
              </Button>
            ) : (
              <Button
                type='primary'
                onClick={(e) => {
                  filterRecords('new', state?.tracer_mappings)
                }}
              >
                {intl.formatMessage({ id: 'dtcs.mappings.showNewRecords' })}
              </Button>
            )}
          </Flex>

          <Flex>
            <Table
              rowKey="rowKey"
              columns={columns}
              dataSource={dataSource}
              showSorterTooltip={false}
              expandable={{
                expandIcon: () => null,
                showExpandColumn: false,
                expandedRowKeys,
                expandedRowRender: (record, index, indent) => (
                  <ExpandedRowRender
                    record={record}
                    index={index}
                    onAccept={confirmTracerMapping}
                  />
                ),
                rowExpandable: () => true,
              }}
              pagination={false}
              locale={{
                emptyText: (
                  <div style={{ padding: '40px' }}>
                    <Empty description={ <RecordsCount count={0} /> } />
                  </div>
                ),
              }}
            />
          </Flex>
        </Row>

        {contextHolder}
      </div>
    </>
  )
}

export default TracerMapping
